import { useState, useEffect } from "react";
import { AlertCircle, Thermometer, Check, ArrowLeft, ArrowRight, ShoppingBag, Shield, Lock, ChevronDown, ChevronUp, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useLanguage } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { ProtectedPdfButton } from "../components/ProtectedPdfButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Equipment list with better icons
const EQUIPMENT = {
  he: [
    { icon: "⚡", name: "בלנדר / מיקסר יד", desc: "לערבוב האבקות והמצב הסופי" },
    { icon: "🥣", name: "כלי קיבולת ערבוב גדול", desc: "קערה גדולה עמידה בחום" },
    { icon: "🌿", name: "חומרי גלם טבעיים", desc: "כל המרכיבים לפי הרשימה" },
    { icon: "🫙", name: "צנצנות זכוכית / פלסטיק קוסמטי", desc: "לאחסון המוצר המוכן" },
    { icon: "🥄", name: "כפיות וכפות מדידה", desc: "למדידה מדויקת" },
    { icon: "✨", name: "מטלית נקייה", desc: "לניגוב וייבוש" },
    { icon: "🍲", name: "כלי למים חמים (בן מארי)", desc: "גדול מכלי הערבוב - להמסת השעווה" },
    { icon: "⚖️", name: "משקל מטבח דיגיטלי", desc: "לדיוק מקסימלי במדידה" },
    { icon: "🌡️", name: "מדחום מטבח", desc: "לבדיקת טמפרטורות (אופציונלי)" }
  ],
  en: [
    { icon: "⚡", name: "Blender / Hand mixer", desc: "For mixing powders and final blend" },
    { icon: "🥣", name: "Large mixing bowl", desc: "Heat-resistant large bowl" },
    { icon: "🌿", name: "Natural raw materials", desc: "All ingredients as listed" },
    { icon: "🫙", name: "Glass / Cosmetic plastic jars", desc: "For storing the finished product" },
    { icon: "🥄", name: "Measuring spoons", desc: "For precise measurement" },
    { icon: "✨", name: "Clean cloth", desc: "For wiping and drying" },
    { icon: "🍲", name: "Hot water container (bain-marie)", desc: "Larger than mixing bowl - for melting wax" },
    { icon: "⚖️", name: "Digital kitchen scale", desc: "For maximum accuracy" },
    { icon: "🌡️", name: "Kitchen thermometer", desc: "For checking temperatures (optional)" }
  ]
};

const PREP_TIPS = {
  he: [
    "וודאו שכל הכלים נקיים ויבשים לפני תחילת העבודה",
    "הכינו את כל המרכיבים מראש ומדדו אותם לפני תחילת התהליך"
  ],
  en: [
    "Make sure all equipment is clean and dry before starting",
    "Prepare and measure all ingredients before beginning the process"
  ]
};

export const DIY = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const equipment = EQUIPMENT[language] || EQUIPMENT.he;
  const prepTips = PREP_TIPS[language] || PREP_TIPS.he;
  
  // Terms acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  
  // User authentication state
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  
  // Check if terms were previously accepted (session storage)
  useEffect(() => {
    const accepted = sessionStorage.getItem('diy-terms-accepted');
    if (accepted === 'true') {
      setTermsAccepted(true);
    }
  }, []);
  
  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = localStorage.getItem('mart_session_token');
        
        const headers = {};
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          headers,
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          localStorage.removeItem('mart_session_token');
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      } finally {
        setIsLoadingAuth(false);
      }
    };
    checkAuth();
  }, []);
  
  const handleLogin = () => {
    // Save current page to return to after login
    localStorage.setItem('mart_auth_redirect', '/diy');
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    sessionStorage.setItem('diy-terms-accepted', 'true');
  };

  // Login Gate Component - shown after terms accepted but before login
  const LoginGate = () => (
    <div className="min-h-screen pt-24 pb-16">
      <SEO page="diy" />
      <section className="px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-[#4A5D43] to-[#3A4A35] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Lock className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#2C3329] mb-4">
              {language === 'he' ? 'עוד צעד אחד...' : 'One more step...'}
            </h1>
            <p className="text-[#6B7266] text-lg">
              {language === 'he' 
                ? 'כדי לצפות בנוסחה המלאה, יש להתחבר עם חשבון Google'
                : 'To view the full formula, please sign in with Google'
              }
            </p>
          </div>
          
          {/* Login Card */}
          <div className="premium-card rounded-2xl p-6 sm:p-8 mb-6 border-2 border-[#E8E4DC]">
            <div className="flex items-center gap-3 mb-4">
              <Check className="h-6 w-6 text-[#4A5D43]" />
              <h2 className="font-serif text-xl text-[#2C3329]">
                {language === 'he' ? 'התקנון אושר!' : 'Terms Accepted!'}
              </h2>
            </div>
            
            <p className="text-[#6B7266] mb-6">
              {language === 'he' 
                ? 'תודה שקראת את התקנון. כעת יש להתחבר כדי לגשת לנוסחה המלאה. ההתחברות מאפשרת לנו לשמור על קהילה איכותית ולהציע לך חוויה מותאמת אישית.'
                : 'Thank you for reading the terms. Now please sign in to access the full formula. This helps us maintain a quality community and provide a personalized experience.'
              }
            </p>
            
            {/* Why login info */}
            <div className="bg-[#F5F2EB] rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-[#4A5D43] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-[#2C3329] text-sm mb-1">
                    {language === 'he' ? 'למה צריך להתחבר?' : 'Why sign in?'}
                  </h4>
                  <ul className="text-xs text-[#6B7266] space-y-1">
                    {(language === 'he' ? [
                      'גישה מלאה לנוסחה והתהליך',
                      'שמירה על קהילה איכותית',
                      'המידע שלך מאובטח ולא משותף'
                    ] : [
                      'Full access to formula and process',
                      'Maintaining a quality community',
                      'Your info is secure and not shared'
                    ]).map((item, idx) => (
                      <li key={idx}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Login Button */}
            <Button 
              onClick={handleLogin}
              className="w-full bg-white text-[#2C3329] border-2 border-[#E8E4DC] hover:bg-[#F5F2EB] rounded-xl py-6 shadow-md hover:shadow-lg transition-all"
            >
              <svg className={`${isRTL ? 'ml-3' : 'mr-3'} h-5 w-5`} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {language === 'he' ? 'התחברות עם Google' : 'Sign in with Google'}
            </Button>
          </div>
          
          {/* Alternative: Buy ready product */}
          <div className="text-center mt-8 pt-8 border-t border-[#E8E4DC]">
            <p className="text-[#6B7266] mb-4">
              {language === 'he' ? 'מעדיפים מוצר מוכן?' : 'Prefer a ready product?'}
            </p>
            <Link to="/product">
              <Button variant="outline" className="rounded-full px-6">
                <ShoppingBag className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {language === 'he' ? 'לעמוד המוצר' : 'View Product'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );

  // Terms Gate Component
  const TermsGate = () => (
    <div className="min-h-screen pt-24 pb-16">
      <SEO page="diy" />
      <section className="px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#4A5D43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="h-10 w-10 text-[#4A5D43]" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#2C3329] mb-4">
              {language === 'he' ? 'לפני שמתחילים...' : 'Before we begin...'}
            </h1>
            <p className="text-[#6B7266] text-lg">
              {language === 'he' 
                ? 'הכנת דאודורנט טבעי בבית דורשת הבנה של החומרים והתהליך. אנא קראו את התקנון לפני שתתחילו.'
                : 'Making natural deodorant at home requires understanding of materials and process. Please read the terms before starting.'
              }
            </p>
          </div>
          
          {/* Terms Summary Card */}
          <div className="premium-card rounded-2xl p-6 sm:p-8 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-6 w-6 text-[#4A5D43]" />
              <h2 className="font-serif text-xl text-[#2C3329]">
                {language === 'he' ? 'נקודות חשובות מהתקנון' : 'Important Terms Summary'}
              </h2>
            </div>
            
            <ul className="space-y-3 mb-6">
              {(language === 'he' ? [
                'המידע באתר מוצג למטרות לימוד בלבד ואינו מהווה ייעוץ רפואי או מקצועי',
                'הכנת מוצרי קוסמטיקה בבית היא באחריות המשתמש בלבד',
                'מומלץ לבצע בדיקת רגישות (Patch Test) לפני שימוש ראשון',
                'יש להימנע משימוש במוצר במקרה של גירוי או תגובה אלרגית',
                'M\'ART אינה אחראית לכל נזק שעלול להיגרם משימוש במידע'
              ] : [
                'Information is provided for educational purposes only, not medical advice',
                'Home cosmetics production is at user\'s own responsibility',
                'Patch test recommended before first use',
                'Discontinue use if irritation or allergic reaction occurs',
                'M\'ART is not liable for any damage from using this information'
              ]).map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-[#C47F64] shrink-0 mt-0.5" />
                  <span className="text-[#6B7266] text-sm">{item}</span>
                </li>
              ))}
            </ul>
            
            {/* Read full terms link */}
            <Link 
              to="/terms" 
              target="_blank"
              onClick={() => setTermsRead(true)}
              className="inline-flex items-center gap-2 text-[#4A5D43] hover:text-[#3A4A35] font-medium text-sm"
            >
              <Shield className="h-4 w-4" />
              {language === 'he' ? 'קראו את התקנון המלא ←' : 'Read full terms →'}
            </Link>
          </div>
          
          {/* Acceptance Checkbox */}
          <div className="premium-card rounded-2xl p-6 mb-6">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={termsRead}
                onChange={(e) => setTermsRead(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-[#E8E4DC] text-[#4A5D43] focus:ring-[#4A5D43]"
              />
              <span className="text-[#2C3329]">
                {language === 'he' 
                  ? 'קראתי את התקנון, מבין/ה את האחריות המוטלת עליי, ומסכים/ה לתנאי השימוש'
                  : 'I have read the terms, understand my responsibilities, and agree to the terms of use'
                }
              </span>
            </label>
          </div>
          
          {/* Accept Button */}
          <Button
            onClick={handleAcceptTerms}
            disabled={!termsRead}
            className={`w-full py-6 rounded-xl text-lg font-medium transition-all ${
              termsRead 
                ? 'bg-[#C47F64] hover:bg-[#b06d53] text-white shadow-lg hover:shadow-xl' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {termsRead ? (
              <>
                <Check className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {language === 'he' ? 'אני מאשר/ת - הראו לי את הנוסחה' : 'I Accept - Show me the formula'}
              </>
            ) : (
              language === 'he' ? 'יש לאשר את התקנון כדי להמשיך' : 'Accept terms to continue'
            )}
          </Button>
          
          {/* Alternative: Buy ready product */}
          <div className="text-center mt-8 pt-8 border-t border-[#E8E4DC]">
            <p className="text-[#6B7266] mb-4">
              {language === 'he' ? 'מעדיפים מוצר מוכן?' : 'Prefer a ready product?'}
            </p>
            <Link to="/product">
              <Button variant="outline" className="rounded-full px-6">
                <ShoppingBag className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {language === 'he' ? 'לעמוד המוצר' : 'View Product'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );

  // If terms not accepted, show the terms gate
  if (!termsAccepted) {
    return <TermsGate />;
  }
  
  // Loading auth state
  if (isLoadingAuth) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#4A5D43] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  // Terms accepted but not logged in - show login gate
  if (!user) {
    return <LoginGate />;
  }

  // Original DIY content (shown after accepting terms AND logging in)
  return (
    <div className="min-h-screen pt-20 pb-12">
      <SEO page="diy" />
      
      {/* User Badge */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mb-3">
        <div className="flex items-center gap-2 text-sm text-[#6B7266]">
          <User className="h-4 w-4" />
          <span>{language === 'he' ? 'מחובר כ:' : 'Logged in as:'}</span>
          <span className="font-medium text-[#2C3329]">{user.name || user.email}</span>
        </div>
      </div>
      
      {/* Header */}
      <section className="px-4 sm:px-6 mb-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            data-testid="diy-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C3329] mb-4 animate-fade-in-up"
          >
            {t.diy.title}
          </h1>
          <p className="text-lg text-[#6B7266] mb-6 animate-fade-in-up animation-delay-200">
            {t.diy.subtitle}
          </p>
          <div className="animate-fade-in-up animation-delay-300">
            <ProtectedPdfButton 
              variant="default" 
              language={language}
              className="bg-[#C47F64] hover:bg-[#b06d53] text-white rounded-full px-8 py-5 text-sm tracking-wide shadow-lg hover:shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Equipment Section - Collapsible */}
      <section className="px-4 sm:px-6 mb-6">
        <div className="max-w-5xl mx-auto">
          <div className="premium-card rounded-2xl overflow-hidden bg-gradient-to-br from-[#FAF8F5] to-[#F5F2EB] border border-[#E8E4DC]">
            {/* Prep Tips - Always visible */}
            <div className="p-6 sm:p-8">
              <div className="p-5 bg-[#4A5D43]/5 rounded-xl border border-[#4A5D43]/10">
                <h3 className="font-serif text-lg text-[#4A5D43] mb-3 flex items-center gap-2">
                  <span className="text-xl">💡</span>
                  {isRTL ? 'לפני שמתחילים' : 'Before You Start'}
                </h3>
                <ul className="space-y-2">
                  {prepTips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-2 text-[#6B7266]">
                      <Check className="h-4 w-4 text-[#4A5D43] mt-1 shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Equipment Header - Clickable to expand with animation */}
            <button
              onClick={() => setEquipmentOpen(!equipmentOpen)}
              className="equipment-expand-btn w-full px-6 sm:px-8 py-5 border-t border-[#E8E4DC] flex items-center justify-between hover:bg-[#4A5D43]/5 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C47F64]/10 rounded-full flex items-center justify-center animate-bounce-subtle">
                  <span className="text-xl">🧰</span>
                </div>
                <div className={`text-${isRTL ? 'right' : 'left'}`}>
                  <h2 className="font-sans text-xl text-[#2C3329] font-semibold">
                    {isRTL ? 'ציוד נדרש' : 'Required Equipment'}
                  </h2>
                  <p className="text-sm text-[#6B7266]">
                    {isRTL ? 'לחצו לצפייה ברשימת הציוד והכלים המלאה' : 'Click to view full equipment list'}
                  </p>
                </div>
              </div>
              <div className={`w-10 h-10 rounded-full bg-[#4A5D43] flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${equipmentOpen ? 'rotate-180 bg-[#C47F64]' : 'animate-pulse-gentle'}`}>
                <ChevronDown className="h-5 w-5 text-white" />
              </div>
            </button>

            {/* Equipment Grid - Collapsible */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${equipmentOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {equipment.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 bg-white rounded-xl border border-[#E8E4DC] hover:border-[#4A5D43]/30 hover:shadow-sm transition-all"
                    >
                      <span className="text-2xl">{item.icon}</span>
                      <div>
                        <p className="font-medium text-[#2C3329] text-sm">{item.name}</p>
                        <p className="text-xs text-[#6B7266]">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ THE FORMULA ══════════════ */}
      <section className="px-4 sm:px-6 mb-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 bg-[#4A5D43]/10 text-[#4A5D43] text-xs font-medium rounded-full mb-4 tracking-wider uppercase">
              {isRTL ? 'הפורמולה' : 'The Formula'}
            </span>
            <h2 data-testid="ingredients-title" className="font-serif text-2xl sm:text-3xl text-[#2C3329]">
              {t.diy.ingredients.title}
            </h2>
          </div>

          {/* Ingredient Cards, Magazine Layout */}
          <div className="grid gap-px bg-[#E8E4DC] rounded-2xl overflow-hidden shadow-sm">
            {t.diy.ingredients.items.map((item, index) => {
              const percentNum = parseFloat(item.percent);
              return (
                <div 
                  key={index}
                  data-testid={`ingredient-${index}`}
                  className="bg-white p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center group hover:bg-[#FAF8F5] transition-colors"
                >
                  {/* Percent Indicator */}
                  <div className="flex sm:flex-col items-center gap-3 sm:gap-1 sm:w-20 shrink-0">
                    <span className="text-2xl sm:text-3xl font-serif font-semibold text-[#2C3329] leading-none">
                      {item.percent.replace('%', '')}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-[#9A9790]">%</span>
                    {/* Bar */}
                    <div className="hidden sm:block w-full h-1.5 bg-[#E8E4DC] rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-[#4A5D43] rounded-full transition-all" style={{ width: `${Math.min(percentNum * 2.5, 100)}%` }} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1.5">
                      <h3 className="font-serif text-lg text-[#2C3329] font-medium">{item.name}</h3>
                      {item.quality && (
                        <span className="text-[11px] text-[#4A5D43] bg-[#4A5D43]/8 px-2 py-0.5 rounded font-medium">{item.quality}</span>
                      )}
                    </div>
                    <p className="text-sm text-[#6B7266] leading-relaxed">{item.role}, {item.action}</p>
                    {item.texture && <p className="text-sm text-[#9A9790] mt-1">{item.texture}</p>}
                    {item.note && (
                      <div className="mt-2 flex items-center gap-2 text-[#C47F64] text-xs font-medium">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span>{item.note}</span>
                      </div>
                    )}
                  </div>

                  {/* Weight + Measure */}
                  <div className="flex sm:flex-col items-center gap-3 sm:gap-0.5 sm:text-right sm:w-32 shrink-0">
                    <span className="font-medium text-[#2C3329] text-sm">{item.volume}</span>
                    <span className="text-xs text-[#9A9790]">{item.amount}</span>
                  </div>
                </div>
              );
            })}

            {/* Total Row */}
            <div className="bg-[#4A5D43]/5 p-4 sm:p-5 flex items-center justify-between">
              <span className="font-medium text-[#2C3329] text-sm">{isRTL ? 'סה״כ' : 'Total'}</span>
              <div className="flex items-center gap-6">
                <span className="font-serif text-lg font-semibold text-[#4A5D43]">100%</span>
                <span className="font-medium text-[#2C3329] text-sm">100g</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#9A9790] text-center mt-3">{t.diy.proportions.note}</p>
        </div>
      </section>

      {/* ══════════════ STABILIZATION TIP ══════════════ */}
      <section className="px-4 sm:px-6 mb-6">
        <div className="max-w-5xl mx-auto">
          <div data-testid="stabilization-tip" className="relative rounded-2xl overflow-hidden border border-[#C47F64]/20 bg-gradient-to-br from-[#FDF8F4] to-[#FAF2EC]">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#C47F64]" />
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="h-5 w-5 text-[#C47F64] shrink-0" />
                <h3 className="font-serif text-base text-[#2C3329] font-medium">
                  {t.diy.stabilizationTip.title}
                </h3>
              </div>
              <p className="text-[#6B7266] text-sm leading-relaxed mb-4">
                {t.diy.stabilizationTip.content}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-white/70 rounded-xl p-4 border border-[#4A5D43]/10">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#4A5D43] shrink-0 mt-0.5" />
                    <p className="text-[#2C3329] text-sm leading-relaxed">{t.diy.stabilizationTip.alternative}</p>
                  </div>
                </div>
                <div className="bg-white/70 rounded-xl p-4 border border-[#4A5D43]/10">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[#4A5D43] shrink-0 mt-0.5" />
                    <p className="text-[#2C3329] text-sm leading-relaxed">{t.diy.stabilizationTip.castorOil}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="px-4 sm:px-6 mb-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 bg-[#C47F64]/10 text-[#C47F64] text-xs font-medium rounded-full mb-4 tracking-wider uppercase">
              {isRTL ? 'התהליך' : 'Process'}
            </span>
            <h2 
              data-testid="process-title"
              className="font-serif text-2xl sm:text-3xl text-[#2C3329]"
            >
              {t.diy.process.title}
            </h2>
          </div>
          
          <div className="space-y-6">
            {t.diy.process.steps.map((step, index) => (
              <div 
                key={step.number}
                data-testid={`step-${step.number}`}
                className="premium-card p-6 sm:p-8 rounded-xl animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} items-start gap-5`}>
                  <div className="step-number shrink-0">
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl text-[#2C3329] mb-3">{step.title}</h3>
                    <p className="text-[#6B7266] leading-relaxed">{step.content}</p>
                    {step.warning && (
                      <div className="mt-4 flex items-start gap-3 p-4 bg-[#C47F64]/5 rounded-lg border border-[#C47F64]/20">
                        <Thermometer className="h-5 w-5 text-[#C47F64] shrink-0 mt-0.5" />
                        <p className="text-sm text-[#C47F64]">{step.warning}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage Instructions */}
      <section className="px-4 sm:px-6 mb-6 py-6 section-warm">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <h2 
              data-testid="usage-title"
              className="font-serif text-2xl sm:text-3xl text-[#2C3329]"
            >
              {t.diy.usage.title}
            </h2>
          </div>
          
          <div className="premium-card p-8 rounded-xl">
            <ol className="list-decimal list-inside space-y-4 text-[#2C3329] mb-6">
              {t.diy.usage.instructions.map((instruction, index) => (
                <li key={index} className="leading-relaxed">{instruction}</li>
              ))}
            </ol>
            <div className="space-y-2 text-sm text-[#6B7266] border-t border-[#E8E4DC] pt-6">
              <p>{t.diy.usage.frequency}</p>
              <p>{t.diy.usage.cleaning}</p>
              <p>{t.diy.usage.storage}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips & Sensitivity */}
      <section className="px-4 sm:px-6 mb-6">
        <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-4">
          {/* Tip */}
          <div className="p-6 rounded-xl bg-gradient-to-br from-[#4A5D43]/5 to-[#4A5D43]/10 border border-[#4A5D43]/20">
            <h3 className="font-serif text-lg text-[#2C3329] mb-3">{t.diy.tips.title}</h3>
            <p className="text-sm text-[#6B7266] leading-relaxed">{t.diy.tips.content}</p>
          </div>
          
          {/* Sensitivity Note */}
          <div className="p-6 rounded-xl bg-gradient-to-br from-[#C47F64]/5 to-[#C47F64]/10 border border-[#C47F64]/20">
            <h3 className="font-serif text-lg text-[#2C3329] mb-3">{t.diy.sensitivity.title}</h3>
            <p className="text-sm text-[#6B7266] leading-relaxed">{t.diy.sensitivity.content}</p>
          </div>
        </div>
      </section>

      {/* Buy Ready-Made CTA - NEW */}
      <section className="px-4 sm:px-6 mb-4">
        <div className="max-w-3xl mx-auto">
          <div className="premium-card rounded-2xl p-6 sm:p-8 text-center bg-gradient-to-br from-[#FAF8F5] to-[#F5F2EB] border-2 border-dashed border-[#4A5D43]/30">
            <div className="w-14 h-14 bg-[#4A5D43]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-7 h-7 text-[#4A5D43]" />
            </div>
            <h3 className="font-serif text-xl sm:text-2xl text-[#2C3329] mb-2">
              {isRTL ? 'מעדיפים מוכן?' : 'Prefer Ready-Made?'}
            </h3>
            <p className="text-[#6B7266] mb-6 max-w-md mx-auto">
              {isRTL 
                ? 'אותה פורמולה בדיוק, מוכנה לשימוש. חוסכים זמן, מקבלים את אותו המוצר.' 
                : 'Same exact formula, ready to use. Save time, get the same product.'}
            </p>
            <Link to="/product">
              <Button 
                className="bg-[#C47F64] hover:bg-[#b06d53] text-white rounded-full px-10 py-6 text-base shadow-lg hover:shadow-xl transition-all"
                data-testid="buy-ready-cta"
              >
                <ShoppingBag className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'לרכישת דאודורנט מוכן' : 'Buy Ready-Made Deodorant'}
                <Arrow className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </Button>
            </Link>
            <p className="text-sm text-[#9A9790] mt-4">
              {isRTL ? '50 מ"ל • 50 ₪ • משלוח עד הבית' : '50ml • ₪50 • Home delivery'}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DIY;
